import { useEffect } from "react";
import { useLenis } from "lenis/react";
import { Check } from "lucide-react";
import { Reveal } from "@/components/common/Reveal";
import { Eyebrow } from "@/components/common/Section";
import { Ridgeline } from "@/components/common/Ridgeline";
import { RentAnalysis } from "@/components/owners/RentAnalysis";

const TERMS = [
  { n: "8%", l: "flat management fee — that's the whole number", off: "" },
  { n: "3 days", l: "average time-to-lease across our list", off: "md:mt-12" },
  { n: "96%", l: "of rent collected on time, every month", off: "md:-mt-6" },
  { n: "30 days", l: "notice to leave — no lock-in, ever", off: "md:mt-8" },
];

const INCLUDED = [
  "Photography, floor plans & listing copy",
  "Marketing on every platform at once",
  "Tenant screening & reference checks",
  "Rent collection & deposit handling",
  "Maintenance coordinated — at cost",
  "A report every Monday at 10am",
  "Move-in & move-out inspections",
  "A real person, 8am–8pm, seven days",
];

export default function Owners() {
  const lenis = useLenis();

  const goAnalysis = () => {
    const el = document.getElementById("analysis");
    if (!el) return;
    if (lenis) lenis.scrollTo(el, { offset: -80 });
    else el.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    if (window.location.hash === "#analysis") {
      const t = setTimeout(goAnalysis, 450);
      return () => clearTimeout(t);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lenis]);

  return (
    <div data-testid="page-owners">
      {/* Header */}
      <header className="relative" style={{ backgroundColor: "#EDE9E0" }} data-nav-theme="light">
        <div className="mx-auto max-w-[1440px] px-6 pb-14 pt-[128px] md:px-10 md:pb-16 md:pt-[168px]">
          <Reveal><Eyebrow>Own with ROAM</Eyebrow></Reveal>
          <Reveal delay={0.05}>
            <h1 className="font-display mt-7 max-w-[16ch]" style={{ fontSize: "clamp(44px,7vw,92px)", color: "#201E1A", lineHeight: 0.98 }}>
              Your property, in <span className="italic" style={{ color: "#C89A4E" }}>steady hands.</span>
            </h1>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="body-measure mt-6 text-[17px] md:text-[18px]" style={{ color: "rgba(32,30,26,0.75)" }}>
              A small team, a short list, and one clear statement a month. We fill it, mind
              it, and keep you posted — so owning is the easy part.
            </p>
          </Reveal>
          <Reveal delay={0.14}>
            <button type="button" onClick={goAnalysis} className="pill pill-pine mt-10 text-base" data-testid="owners-analysis-cta">
              Get a free rent analysis
            </button>
          </Reveal>
        </div>
        <div className="w-full opacity-80" aria-hidden="true"><Ridgeline stroke="#C89A4E" height={90} /></div>
      </header>

      {/* Terms band */}
      <section className="grain relative" style={{ backgroundColor: "#24382E" }} data-nav-theme="dark" data-testid="owners-terms">
        <div className="relative z-10 mx-auto max-w-[1440px] px-6 py-24 md:px-10 md:py-32">
          <Reveal><Eyebrow color="#AEC4C7">The terms, plainly</Eyebrow></Reveal>
          <Reveal delay={0.05}>
            <h2 className="font-display mt-7 max-w-[16ch]" style={{ fontSize: "clamp(32px,4.5vw,60px)", color: "#F8F5EF", lineHeight: 1.02 }}>
              No fine print. No surprises on the statement.
            </h2>
          </Reveal>
          <div className="mt-16 grid grid-cols-1 gap-12 sm:grid-cols-2 lg:grid-cols-4">
            {TERMS.map((t) => (
              <Reveal key={t.n} className={t.off}>
                <div>
                  <p className="font-display" style={{ fontSize: "clamp(44px,5vw,72px)", color: "#AEC4C7", lineHeight: 0.9 }}>{t.n}</p>
                  <p className="mt-3 text-[15px]" style={{ color: "rgba(237,233,224,0.75)" }}>{t.l}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* What's included */}
      <section className="relative" style={{ backgroundColor: "#F8F5EF" }} data-nav-theme="light" data-testid="owners-included">
        <div className="mx-auto max-w-[1440px] px-6 py-24 md:px-10 md:py-32">
          <div className="grid grid-cols-1 gap-12 md:grid-cols-12">
            <div className="md:col-span-4">
              <Reveal><Eyebrow>What's included</Eyebrow></Reveal>
              <Reveal delay={0.05}>
                <h2 className="font-display mt-7" style={{ fontSize: "clamp(32px,4.5vw,60px)", color: "#201E1A", lineHeight: 1.02 }}>
                  Everything, <span className="italic" style={{ color: "#C89A4E" }}>handled.</span>
                </h2>
              </Reveal>
            </div>
            <div className="md:col-span-8">
              <ul className="grid grid-cols-1 gap-x-10 gap-y-5 sm:grid-cols-2">
                {INCLUDED.map((item, i) => (
                  <Reveal key={item} delay={i * 0.04} as="li">
                    <div className="flex items-start gap-3 border-t pt-4" style={{ borderColor: "rgba(32,30,26,0.14)" }}>
                      <Check size={18} style={{ color: "#C89A4E", marginTop: 2 }} strokeWidth={2.4} />
                      <span className="text-[16px]" style={{ color: "#24382E" }}>{item}</span>
                    </div>
                  </Reveal>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Fee line */}
      <section className="relative" style={{ backgroundColor: "#EDE9E0" }} data-nav-theme="light" data-testid="owners-fee">
        <div className="mx-auto max-w-[1440px] px-6 py-20 md:px-10 md:py-28">
          <Reveal>
            <p className="font-display max-w-[20ch]" style={{ fontSize: "clamp(28px,3.6vw,52px)", color: "#24382E", lineHeight: 1.08 }}>
              Trade invoices at cost. Our fee is our fee — <span className="italic" style={{ color: "#C89A4E" }}>and that's it.</span>
            </p>
          </Reveal>
        </div>
      </section>

      <RentAnalysis />
    </div>
  );
}
